import type { useLazyFetchPayments } from '@festina-life/api'
import { dateUtils, translationUtils, typeUtils } from '@festina-life/core'
import {
  FormikDateRangePicker,
  FormikFilterFormButtons,
  FormikForm,
  FormikMultiSelect,
  FormikSelect,
} from '@festina-life/ui/formikForms'
import { FixedWidthInputs } from '@festina-life/ui/forms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { CardBody } from '@festina-life/ui/structure'
import cx from 'classnames'
import dayjs from 'dayjs'

import { ErrorMessage } from '../../../components/errorFallback/ErrorMessage'
import { useEnumOptions } from '../../../hooks/useEnumOptions'
import { useTime } from '../../../hooks/useTime'
import { useHook, useService } from '../../../lenteHooks'
import type { PaymentFilter } from './initialMatchDebtFilter'

type OnSearch = ReturnType<typeof useLazyFetchPayments>['mutateAsync']

const validateKey = typeUtils.createValidator<PaymentFilter>()

type Props = {
  error: Error | null
  onSearch: OnSearch
  isHidden?: boolean
  onReset?: () => void
}

export const MatchPaymentFiltersForm = ({
  error,
  onSearch,
  isHidden,
  onReset,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { data: time } = useTime()
  const paymentTypes = useEnumOptions('PaymentType', 'moneyIn')
  const initialFilterBase = useService('logic.moneyIn.initialMatchDebtFilter')(
    paymentTypes,
  )

  const initialFilter = {
    ...initialFilterBase,
    paymentIntervalFromDate: dateUtils.dateAsServerStr(
      dayjs(time).subtract(1, 'month').toDate(),
    ),
  }

  return (
    <CardBody outerClassName={cx('shrink-0', isHidden && 'hidden')}>
      <ErrorMessage error={error} />
      <FormikForm
        initialValues={initialFilter}
        onSubmit={(values) =>
          onSearch({
            ...values,
            tenantId,
            paymentStatus: 'open',
            paymentTypes: values.paymentTypesArray?.join(',') || undefined,
          })
        }
        shouldSubmitOnMount
        layout="horizontal"
      >
        <FixedWidthInputs>
          <FormikMultiSelect
            name={validateKey('paymentTypesArray')}
            label={translate('moneyIn.match.paymentType')}
            options={paymentTypes}
            renderFn={(x) => translateEnum('moneyIn.enum.PaymentType', x)}
          />
        </FixedWidthInputs>
        <FormikSelect
          name={validateKey('originalPayment')}
          label={translate('moneyIn.match.filter.originalPayment')}
          options={
            // Not using a checkbox as there would be no way to indicate "don't filter this field".
            [true, false]
          }
          renderFn={(x) => translationUtils.translateBoolean(x, translate)}
        />
        <FormikDateRangePicker
          fromDateName={validateKey('paymentIntervalFromDate')}
          toDateName={validateKey('paymentIntervalToDate')}
          label={translate('dateRangePicker.label')}
        />
        <FormikFilterFormButtons onAdditionalReset={onReset} />
      </FormikForm>
    </CardBody>
  )
}
